/**
 * PWA Context Provider
 * Manages PWA installation state, prompts, and service worker updates
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const PWAContext = createContext(null);

// Custom hook for PWA functionality
export const usePWA = () => {
  const context = useContext(PWAContext);
  if (!context) {
    throw new Error('usePWA must be used within a PWAProvider');
  }
  return context;
};

export const PWAProvider = ({ children }) => {
  // Installation states
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [installOutcome, setInstallOutcome] = useState(null);

  // Service Worker states
  const [swRegistration, setSwRegistration] = useState(null);
  const [swUpdateAvailable, setSwUpdateAvailable] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // UI states
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [showUpdatePrompt, setShowUpdatePrompt] = useState(false);
  const [installPromptDismissed, setInstallPromptDismissed] = useState(false);

  // Platform detection
  const [platform, setPlatform] = useState({
    isIOS: false,
    isAndroid: false,
    isDesktop: false,
    isPWA: false,
    browser: 'unknown'
  });

  // Detect platform on mount
  useEffect(() => {
    const ua = navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isAndroid = /Android/.test(ua);
    const isDesktop = !isIOS && !isAndroid;
    const isPWA = window.matchMedia('(display-mode: standalone)').matches ||
                  window.navigator.standalone === true;

    let browser = 'unknown';
    if (/Chrome/.test(ua) && !/Chromium|Edge|OPR|Samsung/.test(ua)) browser = 'chrome';
    else if (/Safari/.test(ua) && !/Chrome/.test(ua)) browser = 'safari';
    else if (/Firefox/.test(ua)) browser = 'firefox';
    else if (/Edge/.test(ua)) browser = 'edge';
    else if (/Samsung/.test(ua)) browser = 'samsung';

    setPlatform({ isIOS, isAndroid, isDesktop, isPWA, browser });
    setIsInstalled(isPWA);

    // Check if user previously dismissed the prompt
    const dismissType = localStorage.getItem('pwa-install-dismiss-type'); // 'remind' or 'permanent'
    const dismissedTime = localStorage.getItem('pwa-install-dismissed-time');

    if (dismissedTime) {
      const hoursSinceDismissed = (Date.now() - parseInt(dismissedTime)) / (1000 * 60 * 60);

      if (dismissType === 'remind') {
        // "Remind Me Later" - show again after 24 hours
        if (hoursSinceDismissed < 24) {
          setInstallPromptDismissed(true);
        } else {
          localStorage.removeItem('pwa-install-dismiss-type');
          localStorage.removeItem('pwa-install-dismissed-time');
        }
      } else if (dismissType === 'permanent') {
        // "Don't Ask Again" - show again after 30 days
        if (hoursSinceDismissed < 24 * 30) {
          setInstallPromptDismissed(true);
        } else {
          localStorage.removeItem('pwa-install-dismiss-type');
          localStorage.removeItem('pwa-install-dismissed-time');
        }
      } else {
        // Legacy or soft dismiss - clear it
        localStorage.removeItem('pwa-install-dismiss-type');
        localStorage.removeItem('pwa-install-dismissed-time');
        localStorage.removeItem('pwa-install-dismissed');
      }
    }
  }, []);

  // Handle beforeinstallprompt event (Chrome/Edge)
  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);

      // Show install prompt after a delay if not dismissed
      if (!installPromptDismissed && !isInstalled) {
        setTimeout(() => {
          setShowInstallPrompt(true);
        }, 3000); // Show after 3 seconds
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, [installPromptDismissed, isInstalled]);

  // Handle app installed event
  useEffect(() => {
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setShowInstallPrompt(false);
      setDeferredPrompt(null);
      localStorage.setItem('pwa-installed', 'true');
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  // Handle service worker updates
  useEffect(() => {
    const handleSwUpdate = (event) => {
      setSwRegistration(event.detail);
      setSwUpdateAvailable(true);
      setShowUpdatePrompt(true);
    };

    window.addEventListener('swUpdate', handleSwUpdate);

    return () => {
      window.removeEventListener('swUpdate', handleSwUpdate);
    };
  }, []);

  // Handle online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Trigger native install prompt (Chrome/Edge)
  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) {
      console.log('No deferred prompt available');
      return { outcome: 'unavailable' };
    }

    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      setInstallOutcome(outcome);

      if (outcome === 'accepted') {
        setIsInstalled(true);
        setShowInstallPrompt(false);
      }

      setDeferredPrompt(null);
      return { outcome };
    } catch (error) {
      console.error('Install prompt error:', error);
      return { outcome: 'error', error };
    }
  }, [deferredPrompt]);

  // Dismiss install prompt
  // type: 'remind' (24 hours), 'permanent' (30 days), or null/undefined (session only)
  const dismissInstallPrompt = useCallback((type = null) => {
    setShowInstallPrompt(false);

    if (type === 'remind' || type === 'permanent') {
      setInstallPromptDismissed(true);
      localStorage.setItem('pwa-install-dismiss-type', type);
      localStorage.setItem('pwa-install-dismissed-time', Date.now().toString());
    }
    // If type is null/undefined, prompt will show again on next page visit
  }, []);

  // Apply service worker update
  const applyUpdate = useCallback(() => {
    if (swRegistration && swRegistration.waiting) {
      // Register listener BEFORE sending SKIP_WAITING to avoid race condition
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        window.location.reload();
      });
      swRegistration.waiting.postMessage({ type: 'SKIP_WAITING' });
      setShowUpdatePrompt(false);
    } else {
      // Fallback: if no waiting worker, force-refresh to get latest
      setShowUpdatePrompt(false);
      window.location.reload();
    }
  }, [swRegistration]);

  // Dismiss update prompt
  const dismissUpdatePrompt = useCallback(() => {
    setShowUpdatePrompt(false);
  }, []);

  // Clear all caches
  const clearCaches = useCallback(async () => {
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(name => caches.delete(name)));
      return true;
    }
    return false;
  }, []);

  // Request notification permission
  const requestNotificationPermission = useCallback(async () => {
    if (!('Notification' in window)) {
      return { permission: 'unsupported' };
    }

    const permission = await Notification.requestPermission();
    return { permission };
  }, []);

  // Check if should show iOS install instructions
  const shouldShowIOSInstallGuide = platform.isIOS &&
                                    platform.browser === 'safari' &&
                                    !isInstalled &&
                                    !installPromptDismissed;

  const value = {
    // Installation
    isInstallable,
    isInstalled,
    installOutcome,
    promptInstall,
    showInstallPrompt,
    dismissInstallPrompt,
    shouldShowIOSInstallGuide,

    // Service Worker
    swUpdateAvailable,
    showUpdatePrompt,
    applyUpdate,
    dismissUpdatePrompt,

    // Network
    isOnline,

    // Platform info
    platform,

    // Utilities
    clearCaches,
    requestNotificationPermission
  };

  return (
    <PWAContext.Provider value={value}>
      {children}
    </PWAContext.Provider>
  );
};

export default PWAContext;
